import { CheckCircleIcon } from '@heroicons/react/solid';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Spinner } from '@yoast/ui-library';
import cx from 'classnames';
import type { MouseEvent } from 'react';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  useGetAnalysisStatus,
  useGetBrand,
} from '../../api/generated/insights';
import type { AnalysisBaseStatus } from '../../api/generated/insights.schemas';
import Divider from '../../components/Divider';
import ProgressPhases from '../../components/ProgressPhases';
import { useBrandAnalysisData } from '../../contexts';
import {
  isAnalysisScheduledMoreThanMinutesAway,
  isAnalysisScheduledWithinMinutes,
} from '../../utils/timeUtils';

const getRefetchIntervalByAnalysisStatus = (
  status: AnalysisBaseStatus | undefined,
) => {
  switch (status) {
    case 'pending':
    case 'processing':
      return 2 * 1000; // if actively analyzing, refetch every x seconds
    case 'completed':
      return false; // stop refetching if completed
    default:
      // fallback: refetch every 10s
      // 'queued' or any other status
      return 10 * 1000;
  }
};

const Analysis: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const params = useParams();

  // brandId may be defined in the URL, and this should take precedence during the setup flow
  const { brandId: contextBrandId } = useBrandAnalysisData();
  const brandId = params.brandId || contextBrandId;

  // Track progress phase when all queries are completed
  const [buildingPhase, setBuildingPhase] = useState<'building' | 'finalizing' | null>(null);

  // fetch brand data so we can display all queries
  const { data: brandData } = useGetBrand(brandId, {
    query: {
      enabled: !!brandId,
      select: ({ data }) => data,
    },
  });

  // analysis status will report which queries are completed
  const { data: analysisStatus } = useGetAnalysisStatus(
    brandId,
    // NOTE: in the future we may need to select a specific analysis ID rather than always use 'latest' here
    'latest',
    {
      query: {
        enabled: !!brandId,
        select: ({ data }) => data,
        refetchInterval: (query) => {
          const analysisData = query?.state?.data?.data;
          return getRefetchIntervalByAnalysisStatus(analysisData?.status);
        },
      },
    },
  );

  const analysisId = analysisStatus?.id;
  const nextStepLink = analysisId
    ? `/brands/${brandId}/analysis/${analysisId}`
    : `/brands/${brandId}`;

  useEffect(() => {
    const isAnalysisInProgress =
      analysisStatus?.status === 'pending' ||
      analysisStatus?.status === 'processing' ||
      analysisStatus?.status === 'completed';
    if (
      brandData?.nextScheduledAnalysis &&
      analysisStatus?.status &&
      isAnalysisScheduledMoreThanMinutesAway(
        brandData.nextScheduledAnalysis,
        5,
      ) &&
      !isAnalysisInProgress
    ) {
      navigate(`/brands/${brandId}`, {
        // replace history to avoid going "back" to the analysis page
        replace: true,
      });
    }
  }, [
    brandData?.nextScheduledAnalysis,
    brandId,
    navigate,
    analysisStatus?.status,
  ]);

  useEffect(() => {
    if (
      brandData?.setupStatus === 'completed' ||
      brandData?.setupStatus === 'firstAnalysisNotScheduled' ||
      (brandData?.setupStatus === 'scheduledFirstAnalysis' &&
        brandData?.firstNextAnalysisOpportunity &&
        !isAnalysisScheduledWithinMinutes(
          brandData.firstNextAnalysisOpportunity,
          2,
        ))
    ) {
      navigate(nextStepLink, { replace: true });
    }
  }, [
    brandData?.firstNextAnalysisOpportunity,
    brandData?.setupStatus,
    nextStepLink,
    navigate,
  ]);

  const totalQueries = brandData?.queries?.length;
  const completedQueries = analysisStatus?.completedQueries?.length || 0;

  // Start timer when all queries are completed to manage "Building analysis..." and "Finalizing..." phases.
  useEffect(() => {
    if (
      analysisStatus?.status === 'processing' &&
      totalQueries &&
      completedQueries === totalQueries
    ) {
      // All queries just completed - start "Building analysis..." phase
      setBuildingPhase('building');

      // After 10 seconds, switch to "Finalizing..." phase
      const timer = setTimeout(() => {
        setBuildingPhase('finalizing');
      }, 10000);

      return () => clearTimeout(timer);
    } else {
      // Reset if queries are no longer all completed or status changed
      setBuildingPhase(null);
    }
  }, [analysisStatus?.status, totalQueries, completedQueries]);

  const progress = (() => {
    if (totalQueries && totalQueries > 0) {
      // Each completed query moves the progress by 80 / totalQueries (0-80%)
      const perQueryProgress = 80 / totalQueries;
      const queriesProgress = completedQueries * perQueryProgress;

      if (analysisStatus?.status === 'completed') {
        return 100; // Done!
      }

      if (analysisStatus?.status === 'processing' &&
          completedQueries === totalQueries &&
          buildingPhase !== null
      ) {
          // Show "Building analysis..." or "Finalizing..." phase
          return buildingPhase === 'building' ? 90 : 95;
      }

      // Cap progress at 80% during query execution
      return Math.round(Math.min(queriesProgress, 80));
    }
    return 0;
  })();

  // Determine whether to show queries based on progress
  const shouldShowQueries = progress <= 80 || progress === 100;

  return (
    <div>
      <div className="yst-space-y-8">
        <ProgressPhases progress={progress} />

        {shouldShowQueries && (
          <>
            <Divider />

            <div className="yst-space-y-8">
              {brandData?.queries?.map(({ id: queryId, query }) => {
                const completed =
                  queryId &&
                  analysisStatus?.completedQueries?.includes(queryId);

                return (
                  <div
                    key={`result-item-${query}`}
                    className={cx(
                      'yst-flex yst-items-center yst-justify-between yst-gap-3',
                    )}
                  >
                    {/* Note: we are no longer showing whether a query was found or not found at this stage */}
                    {completed === true ? (
                      // found
                      <CheckCircleIcon className="yst-h-5 yst-w-5 yst-text-green-400" />
                    ) : (
                      // pending analysis (loading)
                      <Spinner />
                    )}

                    <span className="yst-flex-1 yst-text-sm">{query}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <div className="yst-mb-[-2rem] yst-ml-[-2rem] yst-mr-[-2rem] yst-mt-8 yst-flex yst-justify-end yst-gap-4 yst-rounded-bl-lg yst-rounded-br-lg yst-border-t yst-border-slate-200 yst-bg-slate-50 yst-px-8 yst-py-6">
        <Button
          as={RouterLink}
          to={nextStepLink}
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            if (progress !== 100) {
              e.preventDefault();
              return;
            }
            queryClient.clear();
          }}
          disabled={progress !== 100}
        >
          Continue →
        </Button>
      </div>
    </div>
  );
};

export default Analysis;
